import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getSessionHeaders, ensureSessionHeaders } from '../../lib/serverSession'
import { kycSubmitApiError } from '../../lib/kycSubmitSessionError.js'
import { supabase } from '../../lib/supabase';

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
import { isValidUserId } from '../../lib/userIdExtractor';
import { useCompleteCountryList } from '../../lib/hooks/useCompleteCountryList';
import {
  readPersistedKycStep, persistKycStep, clearPersistedKycStep,
  isInAppCameraSupported, attemptCameraCapture
} from '../../lib/kycClientResilience';
import { isExplicitKycCountry, KYC_COUNTRY_REQUIRED_MESSAGE } from '../../lib/kycRequiredCountry.js';
import { PrivacyPolicy } from '../PrivacyPolicy';
import { KycSelfieExample } from './KycSelfieExample';
import { TermsAndConditions } from '../TermsAndConditions';
import { SELFIE_ID_CONFIRM_LABEL, SELFIE_ID_CONFIRM_REQUIRED, isSelfieIdConfirmed } from '../../lib/kycFieldRejectNotify.js';
import { birthdateAgeYears, dayMonthYearToIsoDate, isoDateToDayMonthYear } from '../../lib/kycDateDisplay.js';
import { KycBirthdateField } from '../admin/KycBirthdateField';

interface KYCDocumentUploadProps {
  userId: number;
  onComplete?: (data: any) => void;
  onCancel?: () => void;
}

interface KYCFormData {
  // Personal Information
  firstName: string;
  lastName: string;
  dateOfBirth: string; // YYYY-MM-DD format
  nationality: string;
  idType: 'passport' | 'drivers_license' | 'national_id_card' | 'other_government_id';
  idNumber: string;
  phoneNumber: string;
  emailAddress: string;

  // Address Information
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  countryCode: string;
  countryName: string;

  // Company Information (optional)
  isCompanyPurchase: boolean;
  companyName?: string;
  companyRegistrationNumber?: string;
  companyRegistrationDocument?: File | null;

  // Consent
  dataConsent: boolean;
  privacyPolicyAccepted: boolean;
}

interface DocumentFile {
  file: File;
  preview: string;
  type: 'id_document' | 'selfie' | 'address_proof';
}

export const KYCDocumentUpload: React.FC<KYCDocumentUploadProps> = ({
  userId,
  onComplete,
  onCancel
}) => {
  const { t } = useTranslation('common');
  // CRITICAL: Validate userId at component entry point
  // This prevents "invalid input syntax for type integer" database errors
  useEffect(() => {
    if (!isValidUserId(userId)) {
      console.error('❌ Invalid userId passed to KYCDocumentUpload:', userId);
      alert('Error: Invalid user session. Please refresh the page and try again.');
    }
  }, [userId]);

  // v7.3.x FIX (KYC progress loss): restore the wizard step after a mobile page
  // reload (opening the camera/gallery often discards the page). See kycClientResilience.
  const [currentStep, setCurrentStep] = useState<number>(() => readPersistedKycStep(userId));
  const [inlineLegalDoc, setInlineLegalDoc] = useState<null | 'privacy' | 'terms'>(null);
  const [selfieIdConfirmed, setSelfieIdConfirmed] = useState(false);
  const [formData, setFormData] = useState<KYCFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '', // YYYY-MM-DD format
    nationality: '', // Empty - user must manually enter their nationality
    idType: 'passport',
    idNumber: '',
    phoneNumber: '',
    emailAddress: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
    countryCode: '',
    countryName: '',
    isCompanyPurchase: false,
    companyName: '',
    companyRegistrationNumber: '',
    companyRegistrationDocument: null as File | null,
    dataConsent: false,
    privacyPolicyAccepted: false
  });
  
  const [documents, setDocuments] = useState<{
    idDocument?: DocumentFile;
    selfie?: DocumentFile;
    addressProof?: DocumentFile;
  }>({});

  const [dobText, setDobText] = useState('');

  useEffect(() => {
    setDobText(isoDateToDayMonthYear(formData.dateOfBirth));
  }, [formData.dateOfBirth]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dataLoaded, setDataLoaded] = useState(false);
  // REMOVED: cameraSupported state - no longer needed, camera availability checked only when button clicked
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [currentCameraType, setCurrentCameraType] = useState<'id_document' | 'selfie' | 'address_proof' | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);

  // KYC Resubmission state
  const [existingKYC, setExistingKYC] = useState<any>(null);
  const [isResubmission, setIsResubmission] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [existingDocuments, setExistingDocuments] = useState<{
    idDocument?: string;
    selfie?: string;
    addressProof?: string;
    companyRegistration?: string;
  }>({});

  // Check if rejection is ONLY due to missing documents (data is correct, just need to upload docs)
  const isDocumentOnlyRejection = isResubmission && (
    rejectionReason.toLowerCase().includes('no documents') ||
    rejectionReason.toLowerCase().includes('documents not uploaded') ||
    rejectionReason.toLowerCase().includes('please upload') ||
    rejectionReason.toLowerCase().includes('missing documents')
  );

  // Real-time validation status for AJAX checks
  const [validationStatus, setValidationStatus] = useState<Record<string, 'checking' | 'valid' | 'invalid' | null>>({
    firstName: null,
    lastName: null,
    idNumber: null,
    phoneNumber: null,
    emailAddress: null,
    streetAddress: null,
    city: null,
    province: null,
    postalCode: null
  });
  
  const fileInputRefs = {
    idDocument: useRef<HTMLInputElement>(null),
    selfie: useRef<HTMLInputElement>(null),
    addressProof: useRef<HTMLInputElement>(null),
    companyRegistration: useRef<HTMLInputElement>(null),
    // Camera-capture inputs (image-only + `capture`), used as the fallback for the
    // "Take Photo/Selfie" buttons. Kept separate from the browse inputs above so
    // "Choose File" opens the native picker reliably — a `capture` directive breaks
    // plain file selection on Samsung Internet / Android WebView.
    idDocumentCamera: useRef<HTMLInputElement>(null),
    selfieCamera: useRef<HTMLInputElement>(null),
    addressProofCamera: useRef<HTMLInputElement>(null)
  };

  // Use complete country list (195+ countries) with Southern Africa priority
  const { countryOptions } = useCompleteCountryList({
    prioritizeCountries: ['ZAF', 'NAM', 'SWZ', 'BWA', 'ZWE', 'MOZ', 'LSO'] // Southern Africa priority
  });

  // Map to the format expected by the component
  const countries = countryOptions.map(option => ({
    code: option.value,
    name: option.label
  }));

  // Helper function to convert country name to nationality
  const countryToNationality = (countryName: string): string => {
    const specialCases: { [key: string]: string } = {
      'United States': 'American',
      'United Kingdom': 'British',
      'United Arab Emirates': 'Emirati',
      'South Africa': 'South African',
      'New Zealand': 'New Zealander',
      'Saudi Arabia': 'Saudi',
      'North Korea': 'North Korean',
      'South Korea': 'South Korean',
      'Czech Republic': 'Czech',
      'Dominican Republic': 'Dominican',
      'Central African Republic': 'Central African',
      'Democratic Republic of the Congo': 'Congolese',
      'Republic of the Congo': 'Congolese',
      'Côte d\'Ivoire': 'Ivorian',
      'Equatorial Guinea': 'Equatorial Guinean',
      'Guinea-Bissau': 'Bissau-Guinean',
      'Papua New Guinea': 'Papua New Guinean',
      'Trinidad and Tobago': 'Trinidadian/Tobagonian',
      'Bosnia and Herzegovina': 'Bosnian/Herzegovinian',
      'Antigua and Barbuda': 'Antiguan/Barbudan',
      'Saint Kitts and Nevis': 'Kittitian/Nevisian',
      'Saint Vincent and the Grenadines': 'Vincentian',
      'São Tomé and Príncipe': 'São Toméan'
    };

    if (specialCases[countryName]) {
      return specialCases[countryName];
    }

    // Default: add "n" or "ian" to country name
    if (countryName.endsWith('a')) {
      return countryName + 'n';
    } else if (countryName.endsWith('y')) {
      return countryName.slice(0, -1) + 'ian';
    } else if (countryName.endsWith('land')) {
      return countryName.slice(0, -4) + 'lander';
    } else if (countryName.endsWith('stan')) {
      return countryName + 'i';
    } else {
      return countryName + 'an';
    }
  };

  // Generate complete nationality list from all countries and sort alphabetically
  const nationalities = countryOptions
    .map(option => countryToNationality(option.label))
    .sort((a, b) => a.localeCompare(b));

  const getIdTypeOptions = () => {
    // Universal ID type options - same for all countries
    return [
      { value: 'passport', label: 'Passport' },
      { value: 'drivers_license', label: 'Driver\'s License' },
      { value: 'national_id_card', label: 'National ID Card' },
      { value: 'other_government_id', label: 'Other Government-Issued ID' }
    ];
  };

  const getIdNumberLabel = (idType: string) => {
    switch (idType) {
      case 'passport': return 'Passport Number';
      case 'drivers_license': return 'Driver\'s License Number';
      case 'national_id_card': return 'National ID Card Number';
      case 'other_government_id': return 'Government ID Number';
      default: return 'ID Number';
    }
  };

  const getIdNumberPlaceholder = (_idType: string) => {
    return 'e.g., AB123456, 1234 567890, 12-345-6789';
  };

  const getIdDocumentTitle = (idType: string) => {
    switch (idType) {
      case 'passport': return 'Passport Document';
      case 'drivers_license': return 'Driver\'s License Document';
      case 'national_id_card': return 'National ID Card Document';
      case 'other_government_id': return 'Government ID Document';
      default: return 'ID Document';
    }
  };

  const getIdDocumentDescription = (idType: string) => {
    switch (idType) {
      case 'passport': return 'passport';
      case 'drivers_license': return 'driver\'s license';
      case 'national_id_card': return 'national ID card';
      case 'other_government_id': return 'government-issued ID';
      default: return 'identification document';
    }
  };

  const getIdTypeDisplayLabel = (idType: string) => {
    switch (idType) {
      case 'passport': return 'Passport';
      case 'drivers_license': return 'Driver\'s License';
      case 'national_id_card': return 'National ID Card';
      case 'other_government_id': return 'Other Government-Issued ID';
      case 'national_id': return 'National ID'; // Legacy support
      default: return idType || 'Not specified';
    }
  };

  // Load user data for auto-population AND existing KYC data for resubmission
  const loadUserData = useCallback(async () => {
    if (dataLoaded) return;

    try {
      console.log('🔍 KYC Document Upload: Loading user data for auto-population...');
      console.log('🔍 KYC Document Upload: User ID:', userId);

      // Use API endpoint to load KYC and user data
      const response = await fetch(`${API_BASE_URL}/api/user/kyc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'load_user_kyc_data'
        })
      });

      const result = await response.json();
      if (!response.ok) {
        console.error('❌ KYC Document Upload: API error:', result.error);
        return;
      }

      const kycData = result.data?.kycData;
      const userData = result.data?.userData;

      // If user has a rejected KYC OR admin-unlocked KYC (kyc_editable=true), load it for resubmission
      if (kycData && (kycData.kyc_status === 'rejected' || kycData.kyc_editable === true)) {
        console.log('🔄 KYC Document Upload: Found REJECTED KYC record - enabling resubmission mode');
        console.log('🔄 Rejection reason:', kycData.rejection_reason);

        const rejectionReasonText = kycData.rejection_reason || 'Your KYC application was rejected. Please review and correct the information below.';

        // Check if rejection is ONLY due to missing documents
        const isDocOnlyRejection = (
          rejectionReasonText.toLowerCase().includes('no documents') ||
          rejectionReasonText.toLowerCase().includes('documents not uploaded') ||
          rejectionReasonText.toLowerCase().includes('please upload') ||
          rejectionReasonText.toLowerCase().includes('missing documents')
        );

        setExistingKYC(kycData);
        setIsResubmission(true);
        setRejectionReason(rejectionReasonText);

        // Map ID type from database to form value
        const idTypeReverseMapping: { [key: string]: string } = {
          'national_id': 'national_id_card',
          'passport': 'passport',
          'drivers_license': 'drivers_license',
          'other_government_id': 'other_government_id'
        };

        // Pre-populate form with existing KYC data
        setFormData(prev => ({
          ...prev,
          firstName: kycData.first_name || '',
          lastName: kycData.last_name || '',
          dateOfBirth: kycData.date_of_birth || '', // Pre-populate date of birth from existing data
          nationality: kycData.nationality || '',
          idType: (idTypeReverseMapping[kycData.id_type] || 'passport') as any,
          idNumber: kycData.id_number || '', // Show actual ID number for document-only rejections
          phoneNumber: kycData.phone_number || '',
          emailAddress: kycData.email_address || '',
          streetAddress: kycData.street_address || '',
          city: kycData.city || '',
          province: kycData.province || '',
          postalCode: kycData.postal_code || '',
          countryCode: kycData.country_code || '',
          countryName: kycData.country_name || '',
          isCompanyPurchase: kycData.is_company_purchase || false,
          companyName: kycData.company_name || '',
          companyRegistrationNumber: kycData.company_registration_number || '',
          dataConsent: kycData.data_consent_given || false,
          privacyPolicyAccepted: kycData.privacy_policy_accepted || false
        }));

        // If document-only rejection, skip to Step 3 (document upload)
        if (isDocOnlyRejection) {
          console.log('📄 Document-only rejection detected - skipping to Step 3 (Document Upload)');
          setCurrentStep(3);
        }

        // Check for existing documents in storage using regular supabase client
        try {
          const { data: files } = await supabase.storage
            .from('kyc')
            .list('', {
              search: `${userId}_`
            });

          if (files && files.length > 0) {
            const docs: any = {};
            files.forEach((file: any) => {
              if (file.name.includes('_idDocument_')) docs.idDocument = file.name;
              if (file.name.includes('_selfie_')) docs.selfie = file.name;
              if (file.name.includes('_addressProof_')) docs.addressProof = file.name;
              if (file.name.includes('_companyRegistration_')) docs.companyRegistration = file.name;
            });
            setExistingDocuments(docs);
            console.log('📄 Found existing documents:', docs);
          }
        } catch (storageError) {
          console.warn('⚠️ Could not load existing documents:', storageError);
        }

        console.log('✅ KYC Document Upload: Rejected KYC data loaded for resubmission');
        setDataLoaded(true);
        return; // Skip loading from users table
      }

      // If no rejected KYC, use userData from API response
      if (userData) {
        console.log('✅ KYC Document Upload: User data loaded from database:', {
          full_name: userData.full_name,
          email: userData.email,
          phone: userData.phone,
          country_of_residence: userData.country_of_residence
        });

        // Parse full name
        const nameParts = userData.full_name?.split(' ') || [];
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        console.log('🔍 KYC Document Upload: Split name - First:', firstName, 'Last:', lastName);

        setFormData(prev => ({
          ...prev,
          firstName: firstName,
          lastName: lastName,
          emailAddress: userData.email || '',
          phoneNumber: userData.phone || '', // Fixed: use 'phone' not 'phone_number'
          // Country stays empty until the user picks it. Do not default ZAF.
        }));

        console.log('✅ KYC Document Upload: Form data auto-populated:', {
          firstName,
          lastName,
          emailAddress: userData.email,
          phoneNumber: userData.phone,
          countryCode: '(empty - user must select)',
          nationality: '(empty - user must enter manually)'
        });

        setDataLoaded(true);
      } else {
        console.warn('⚠️ KYC Document Upload: No user data found in database');
      }
    } catch (error) {
      console.error('❌ KYC Document Upload: Error loading user data:', error);
    }
  }, [userId, dataLoaded, countries]);

  useEffect(() => {
    loadUserData();
    // CRITICAL FIX: Do NOT check camera support on mount - this triggers permission popup on mobile
    // Camera support will be checked when user clicks "Take Photo" button
  }, [loadUserData]);

  // v7.3.x FIX (KYC progress loss): persist the current wizard step so a mobile page
  // reload (e.g. after opening the camera/gallery) returns the user to where they were.
  useEffect(() => {
    persistKycStep(userId, currentStep);
  }, [currentStep, userId]);

  // REMOVED: checkCameraSupport() function
  // Camera support is now checked ONLY when user clicks "Take Photo" button
  // This prevents the permission popup from appearing on page load or during text input

  // Compress image files using Canvas API (works on mobile Safari/Chrome)
  const compressImage = (file: File, maxDim: number, quality: number): Promise<{ blob: Blob; width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round(height * (maxDim / width));
              width = maxDim;
            } else {
              width = Math.round(width * (maxDim / height));
              height = maxDim;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas context unavailable')); return; }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) { reject(new Error('Canvas toBlob returned null')); return; }
              resolve({ blob, width, height });
            },
            'image/jpeg',
            quality
          );
        } catch (e) { reject(e); }
      };
      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Helper function to upload KYC document via API
  const uploadKYCDocumentViaAPI = async (file: File, documentType: string): Promise<{ documentRef: string, storagePath: string, fileName: string }> => {
    const isImage = /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type);
    let uploadBlob: Blob = file;
    let uploadFileName = file.name;
    let uploadFileType = file.type;

    if (isImage) {
      // Compress image: max 1600px, JPEG at 0.75 quality to stay under Vercel 4.5MB body limit
      console.log(`🖼️ ${documentType} original image: ${(file.size / 1024).toFixed(0)} KB`);
      const { blob } = await compressImage(file, 1600, 0.75);
      console.log(`🖼️ ${documentType} compressed: ${(file.size / 1024).toFixed(0)} KB → ${(blob.size / 1024).toFixed(0)} KB (${((1 - blob.size / file.size) * 100).toFixed(0)}% reduction)`);
      uploadBlob = blob;
      uploadFileType = 'image/jpeg';
      uploadFileName = file.name.replace(/\.[^.]+$/, '.jpg');
    } else {
      console.log(`📄 ${documentType} non-image file (${file.type}), skipping compression: ${(file.size / 1024).toFixed(0)} KB`);
    }

    // Convert to base64 for server-side upload
    const arrayBuffer = await uploadBlob.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
    const fileBase64 = `data:${uploadFileType};base64,${base64}`;
    console.log(`📤 ${documentType} base64 payload: ${(fileBase64.length / 1024).toFixed(0)} KB`);

    const response = await fetch(`${API_BASE_URL}/api/user/kyc`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({
        userId,
        operation: 'upload_kyc_document',
        data: {
          fileBase64,
          fileName: uploadFileName,
          fileType: uploadFileType,
          fileSize: uploadBlob.size,
          documentType
        }
      })
    });

    // Safely parse response - server may return non-JSON (e.g. "Request Entity Too Large")
    let result;
    const responseText = await response.text();
    try {
      result = JSON.parse(responseText);
    } catch {
      // Server returned non-JSON (likely 413 Request Entity Too Large)
      console.error(`❌ Non-JSON response for ${documentType}:`, responseText.substring(0, 200));
      if (response.status === 413 || responseText.toLowerCase().includes('too large') || responseText.toLowerCase().includes('request entity')) {
        throw new Error('File is too large even after compression. Please use a smaller image.');
      }
      throw new Error(`Upload failed: server returned an unexpected response (HTTP ${response.status})`);
    }

    if (!response.ok) {
      throw new Error(result.error || 'Upload failed');
    }

    return result.data;
  };

  // Trigger the native camera/photo-picker <input capture> fallback for a given doc type.
  const triggerCameraFallback = (type: 'id_document' | 'selfie' | 'address_proof') => {
    const refKey = type === 'id_document' ? 'idDocumentCamera' : type === 'address_proof' ? 'addressProofCamera' : 'selfieCamera';
    const input = fileInputRefs[refKey as keyof typeof fileInputRefs];
    input.current?.click();
  };

  // v7.3.x FIX (dead "Take Photo" button): delegated to attemptCameraCapture, which
  // guards for missing/blocked mediaDevices, triggers the native fallback, and always
  // surfaces a message so the button is never silently unresponsive.
  const captureFromCamera = (type: 'id_document' | 'selfie' | 'address_proof') =>
    attemptCameraCapture(type, {
      onStream: (stream) => {
        setCameraStream(stream);
        setCurrentCameraType(type);
        setShowCameraModal(true);
        setCapturedPhoto(null);
      },
      onFallback: () => triggerCameraFallback(type),
      onMessage: (message) => setErrors(prev => {
        const next = { ...prev };
        if (message) next.camera = message; else delete next.camera;
        return next;
      }),
    });

  const closeCameraModal = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
    setCurrentCameraType(null);
    setCapturedPhoto(null);
  };

  const capturePhoto = () => {
    const video = document.getElementById('camera-video') as HTMLVideoElement;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (video && context) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);

      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(photoDataUrl);
    }
  };

  const usePhoto = () => {
    if (capturedPhoto && currentCameraType) {
      // Convert data URL to blob without using fetch (CSP-friendly)
      const dataURLtoBlob = (dataURL: string) => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
      };

      const blob = dataURLtoBlob(capturedPhoto);
      const file = new File([blob], `${currentCameraType}_${Date.now()}.jpg`, { type: 'image/jpeg' });
      handleFileUpload(currentCameraType, file);
      closeCameraModal();
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
  };

  // Normalise a date-of-birth value to a strict YYYY-MM-DD string, or null.
  // Returns null for empty/whitespace/malformed input so the submit payload
  // NEVER sends "" to the `date NOT NULL` column (which caused the Postgres
  // error `invalid input syntax for type date: ""`). Does not shift timezones:
  // it validates the literal YYYY-MM-DD shape and confirms it is a real date.
  const normalizeDateOfBirth = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const trimmed = value.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
    const [y, m, d] = trimmed.split('-').map(Number);
    const parsed = new Date(Date.UTC(y, m - 1, d));
    if (
      parsed.getUTCFullYear() !== y ||
      parsed.getUTCMonth() !== m - 1 ||
      parsed.getUTCDate() !== d
    ) {
      return null;
    }
    return trimmed;
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

      // Date of Birth validation - CRITICAL LEGAL COMPLIANCE REQUIREMENT
      const typedDob = dobText.trim();
      const typedDobIso = typedDob ? dayMonthYearToIsoDate(typedDob) : '';
      if (!typedDob) {
        newErrors.dateOfBirth = 'Date of birth is required';
      } else if (!typedDobIso) {
        newErrors.dateOfBirth = 'Use day/month/year, for example 12/10/1992';
      } else {
        const age = birthdateAgeYears(typedDobIso);
        if (age == null || age < 0) {
          newErrors.dateOfBirth = 'Date of birth cannot be in the future';
        } else if (age < 18) {
          newErrors.dateOfBirth = 'You must be at least 18 years old to complete KYC verification and purchase shares';
        } else if (formData.dateOfBirth !== typedDobIso) {
          setFormData(prev => ({ ...prev, dateOfBirth: typedDobIso }));
        }
      }

      if (!formData.nationality.trim()) newErrors.nationality = 'Nationality is required';
      if (!formData.idNumber.trim()) newErrors.idNumber = 'ID number is required';
      if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
      if (!formData.emailAddress.trim()) newErrors.emailAddress = 'Email address is required';

      // Validate company fields if company purchase is selected
      if (formData.isCompanyPurchase) {
        if (!formData.companyName?.trim()) newErrors.companyName = 'Company name is required';
        if (!formData.companyRegistrationNumber?.trim()) newErrors.companyRegistrationNumber = 'Company registration number is required';
        if (!formData.companyRegistrationDocument) newErrors.companyRegistrationDocument = 'Company registration document is required';
      }

      // Validate ID number - universal validation (no country-specific rules)
      if (!formData.idNumber.trim()) {
        newErrors.idNumber = 'ID number is required';
      } else if (formData.idNumber.length > 25) {
        newErrors.idNumber = 'ID number must not exceed 25 characters';
      }
      // Allow alphanumeric, spaces, hyphens, slashes - no other restrictions
    }

    if (step === 2) {
      if (!formData.streetAddress.trim()) newErrors.streetAddress = 'Street address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.province.trim()) newErrors.province = 'Province/State is required';
      if (!formData.postalCode.trim()) newErrors.postalCode = 'Postal code is required';
      if (!isExplicitKycCountry(formData.countryCode, formData.countryName)) {
        newErrors.countryCode = KYC_COUNTRY_REQUIRED_MESSAGE;
      }
    }

    if (step === 3) {
      if (!documents.idDocument) newErrors.idDocument = 'ID document is required';
      if (!documents.selfie) newErrors.selfie = 'Selfie is required';
      if (!documents.addressProof) newErrors.addressProof = 'Address proof is required';
      if (formData.isCompanyPurchase && !formData.companyRegistrationDocument) {
        newErrors.companyRegistrationDocument = 'Company registration document is required';
      }
      if ((documents.selfie || (isResubmission && existingDocuments.selfie)) && !isSelfieIdConfirmed(selfieIdConfirmed)) {
        newErrors.selfieIdConfirm = SELFIE_ID_CONFIRM_REQUIRED;
      }
    }

    if (step === 4) {
      if (!formData.dataConsent) newErrors.dataConsent = 'Data consent is required';
      if (!formData.privacyPolicyAccepted) newErrors.privacyPolicyAccepted = 'Privacy policy acceptance is required';
    }

    setErrors(newErrors);

    // If there are errors, show an alert to notify the user
    if (Object.keys(newErrors).length > 0) {
      alert(`⚠️ Please fill in all required fields:\n\n${Object.values(newErrors).join('\n')}`);
    }

    return Object.keys(newErrors).length === 0;
  };

  // Real-time duplicate checking functions
  const checkEmailDuplicate = async (email: string) => {
    if (!email.trim() || !email.includes('@')) return;

    setValidationStatus(prev => ({ ...prev, emailAddress: 'checking' }));
    try {
      // SECURITY FIX: Use regular supabase client, not service role on client-side
      // Use maybeSingle() instead of single() to avoid 406 errors when no data exists
      const { data, error } = await supabase
        .from('kyc_information')
        .select('user_id')
        .eq('email_address', email.trim().toLowerCase())
        .maybeSingle();

      if (error) {
        console.error('Email check error:', error);
        setValidationStatus(prev => ({ ...prev, emailAddress: null }));
        return;
      }

      if (data) {
        setValidationStatus(prev => ({ ...prev, emailAddress: 'invalid' }));
        // Don't show error for document-only rejections (user's own data is correct)
        if (!isDocumentOnlyRejection) {
          setErrors(prev => ({ ...prev, emailAddress: '❌ This email address has already been used for KYC verification' }));
        }
      } else {
        setValidationStatus(prev => ({ ...prev, emailAddress: 'valid' }));
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.emailAddress;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Email duplicate check error:', error);
      setValidationStatus(prev => ({ ...prev, emailAddress: null }));
    }
  };

  const checkPhoneDuplicate = async (phone: string) => {
    if (!phone.trim() || phone.length < 7) return;

    setValidationStatus(prev => ({ ...prev, phoneNumber: 'checking' }));
    try {
      const normalizedPhone = phone.replace(/[\s\-()]/g, '');
      // SECURITY FIX: Use regular supabase client, not service role on client-side
      // Use maybeSingle() instead of single() to avoid 406 errors when no data exists
      const { data, error } = await supabase
        .from('kyc_information')
        .select('user_id')
        .eq('phone_number', normalizedPhone)
        .maybeSingle();

      if (error) {
        console.error('Phone check error:', error);
        setValidationStatus(prev => ({ ...prev, phoneNumber: null }));
        return;
      }

      if (data) {
        setValidationStatus(prev => ({ ...prev, phoneNumber: 'invalid' }));
        setErrors(prev => ({ ...prev, phoneNumber: '❌ This phone number has already been used for KYC verification' }));
      } else {
        setValidationStatus(prev => ({ ...prev, phoneNumber: 'valid' }));
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.phoneNumber;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Phone duplicate check error:', error);
      setValidationStatus(prev => ({ ...prev, phoneNumber: null }));
    }
  };

  const checkIdNumberDuplicate = async (idNumber: string) => {
    if (!idNumber.trim() || idNumber.length < 6) return;

    setValidationStatus(prev => ({ ...prev, idNumber: 'checking' }));
    try {
      const idNumberHash = await hashIdNumber(idNumber);

      console.log('🔍 ID Duplicate Check:', {
        idNumber: idNumber.substring(0, 3) + '***',
        hash: idNumberHash.substring(0, 10) + '...'
      });

      // SECURITY FIX: Use regular supabase client, not service role on client-side
      // Use maybeSingle() instead of single() to avoid 406 errors when no data exists
      const { data, error } = await supabase
        .from('kyc_information')
        .select('user_id')
        .eq('id_number_hash', idNumberHash)
        .maybeSingle();

      if (error) {
        console.error('ID check error:', error);
        setValidationStatus(prev => ({ ...prev, idNumber: null }));
        return;
      }

      if (data) {
        console.log('❌ ID duplicate found:', data);
        setValidationStatus(prev => ({ ...prev, idNumber: 'invalid' }));
        setErrors(prev => ({ ...prev, idNumber: '❌ This ID number has already been used for KYC verification' }));
      } else {
        console.log('✅ ID is unique');
        setValidationStatus(prev => ({ ...prev, idNumber: 'valid' }));
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.idNumber;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('ID duplicate check error:', error);
      setValidationStatus(prev => ({ ...prev, idNumber: null }));
    }
  };

  const checkNameDuplicate = async (firstName: string, lastName: string) => {
    if (!firstName.trim() || !lastName.trim()) return;

    setValidationStatus(prev => ({ ...prev, firstName: 'checking', lastName: 'checking' }));
    try {
      // SECURITY FIX: Use regular supabase client, not service role on client-side
      // Use maybeSingle() instead of single() to avoid 406 errors when no data exists
      const { data, error } = await supabase
        .from('kyc_information')
        .select('user_id')
        .ilike('first_name', firstName.trim())
        .ilike('last_name', lastName.trim())
        .maybeSingle();

      if (error) {
        console.error('Name check error:', error);
        setValidationStatus(prev => ({ ...prev, firstName: null, lastName: null }));
        return;
      }

      if (data) {
        setValidationStatus(prev => ({ ...prev, firstName: 'invalid', lastName: 'invalid' }));
        // Don't show error for document-only rejections (user's own data is correct)
        if (!isDocumentOnlyRejection) {
          setErrors(prev => ({ ...prev, firstName: '❌ This name combination has already been used for KYC verification' }));
        }
      } else {
        setValidationStatus(prev => ({ ...prev, firstName: 'valid', lastName: 'valid' }));
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.firstName;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Name duplicate check error:', error);
      setValidationStatus(prev => ({ ...prev, firstName: null, lastName: null }));
    }
  };

  // Real-time validation for address fields (Step 2)
  const validateAddressField = (field: string, value: string) => {
    if (!value.trim() || value.length < 3) {
      setValidationStatus(prev => ({ ...prev, [field]: null }));
      return;
    }

    // Simple validation - just check if field has content
    // In a real app, you might check against a database of valid addresses
    setValidationStatus(prev => ({ ...prev, [field]: 'valid' }));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  // Debounced validation triggers
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.emailAddress) {
        checkEmailDuplicate(formData.emailAddress);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.emailAddress]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.phoneNumber) {
        checkPhoneDuplicate(formData.phoneNumber);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.phoneNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.idNumber) {
        checkIdNumberDuplicate(formData.idNumber);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.idNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.firstName && formData.lastName) {
        checkNameDuplicate(formData.firstName, formData.lastName);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.firstName, formData.lastName]);

  // Debounced validation for address fields
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.streetAddress) {
        validateAddressField('streetAddress', formData.streetAddress);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.streetAddress]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.city) {
        validateAddressField('city', formData.city);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.city]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.province) {
        validateAddressField('province', formData.province);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.province]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.postalCode) {
        validateAddressField('postalCode', formData.postalCode);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.postalCode]);

  const handleFileUpload = useCallback((type: 'id_document' | 'selfie' | 'address_proof', file: File) => {
    // Map parameter names to state keys
    const stateKeyMap = {
      'id_document': 'idDocument',
      'selfie': 'selfie',
      'address_proof': 'addressProof'
    } as const;

    const stateKey = stateKeyMap[type];

    console.log(`📤 Handling file upload for ${type}:`, {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified
    });

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];

    if (file.size > maxSize) {
      const errorMsg = 'File size must be less than 10MB';
      console.error(`❌ ${errorMsg}`, { size: file.size, maxSize });
      setErrors(prev => ({ ...prev, [type]: errorMsg }));
      return;
    }

    // Enhanced file type validation - check both MIME type and file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
    const isValidByExtension = validExtensions.includes(fileExtension || '');
    const isValidByMimeType = allowedTypes.includes(file.type);

    console.log(`🔍 File validation:`, {
      extension: fileExtension,
      isValidByExtension,
      mimeType: file.type,
      isValidByMimeType
    });

    // Accept if either MIME type OR extension is valid (handles camera captures that may have incorrect MIME type)
    if (!isValidByMimeType && !isValidByExtension) {
      const errorMsg = 'Only JPEG, PNG, and PDF files are allowed';
      console.error(`❌ ${errorMsg}`, { mimeType: file.type, extension: fileExtension });
      setErrors(prev => ({ ...prev, [type]: errorMsg }));
      return;
    }

    // Create preview
    const preview = URL.createObjectURL(file);

    console.log(`✅ File validation passed, creating preview:`, { preview });

    setDocuments(prev => ({
      ...prev,
      [stateKey]: { file, preview, type }
    }));
    if (type === 'selfie') {
      setSelfieIdConfirmed(false);
    }

    // Clear error
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[type];
      return newErrors;
    });
  }, []);

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    try {
      setLoading(true);

      // ============================================================
      // CRITICAL: MANDATORY DOCUMENT VALIDATION (CANNOT BE BYPASSED)
      // This check runs BEFORE any database operations to ensure
      // users cannot submit KYC without all required documents
      // ============================================================
      const missingDocsBeforeSubmit: string[] = [];

      // For NEW submissions: ALL documents MUST be uploaded
      // For RESUBMISSIONS: Must have either new upload OR existing document
      if (!documents.idDocument && !(isResubmission && existingDocuments.idDocument)) {
        missingDocsBeforeSubmit.push('ID Document');
      }
      if (!documents.selfie && !(isResubmission && existingDocuments.selfie)) {
        missingDocsBeforeSubmit.push('Selfie');
      }
      if (!documents.addressProof && !(isResubmission && existingDocuments.addressProof)) {
        missingDocsBeforeSubmit.push('Proof of Address');
      }
      if (formData.isCompanyPurchase && !formData.companyRegistrationDocument) {
        missingDocsBeforeSubmit.push('Company Registration Document');
      }

      if (missingDocsBeforeSubmit.length > 0) {
        setLoading(false);
        const errorMsg = `⚠️ REQUIRED DOCUMENTS MISSING:\n\n${missingDocsBeforeSubmit.join('\n')}\n\nYou MUST upload all required documents before submitting your KYC application. Please go back to Step 3 and upload the missing documents.`;
        alert(errorMsg);
        setErrors({ submit: `Missing required documents: ${missingDocsBeforeSubmit.join(', ')}` });
        return;
      }
      if (!isSelfieIdConfirmed(selfieIdConfirmed)) {
        setLoading(false);
        alert(`⚠️ ${SELFIE_ID_CONFIRM_REQUIRED}`);
        setErrors({ selfieIdConfirm: SELFIE_ID_CONFIRM_REQUIRED });
        return;
      }
      // ============================================================

      // ============================================================
      // CRITICAL: DATE OF BIRTH INTEGRITY GUARD (18+ LEGAL REQUIREMENT)
      // handleSubmit only runs validateStep(4) (consent boxes), so on a
      // document-only rejection resubmission (which starts at Step 3 and
      // renders DOB read-only) an empty/malformed dateOfBirth would reach
      // the payload and hit the `date NOT NULL` column as "", producing the
      // Postgres error `invalid input syntax for type date: ""`. Normalise
      // to a strict YYYY-MM-DD here and refuse to submit a blank DOB with a
      // clear message that routes the user back to Step 1, instead of a raw
      // SQL error. See normalizeDateOfBirth().
      // ============================================================
      if (!isExplicitKycCountry(formData.countryCode, formData.countryName)) {
        setLoading(false);
        setCurrentStep(2);
        setErrors({ countryCode: KYC_COUNTRY_REQUIRED_MESSAGE });
        alert('⚠️ ' + KYC_COUNTRY_REQUIRED_MESSAGE);
        return;
      }

      const normalizedDateOfBirth = normalizeDateOfBirth(formData.dateOfBirth);
      if (!normalizedDateOfBirth) {
        setLoading(false);
        setCurrentStep(1);
        setErrors({ dateOfBirth: 'Date of birth is required. Please enter your date of birth before submitting.' });
        alert('⚠️ Date of birth is required. Please go back to Step 1 and enter your date of birth before submitting your KYC application.');
        return;
      }
      // ============================================================

      // Pass any known existing record ID as a hint to the server.
      // The SERVER now makes the final INSERT vs UPDATE decision by checking the DB
      // with service role (bypasses RLS). This eliminates the bug where client-side
      // queries returned null due to RLS, causing INSERT on existing records.
      const existingRecordId = existingKYC?.id || null;

      // Hash the ID number for duplicate checking
      const idNumberHash = await hashIdNumber(formData.idNumber);

      // Check if this ID number is already registered
      // SECURITY FIX: Use regular supabase client, not service role on client-side
      // IMPORTANT: Exclude current user's own record when checking for duplicates
      const { data: duplicateID } = await supabase
        .from('kyc_information')
        .select('id, user_id')
        .eq('id_number_hash', idNumberHash)
        .neq('user_id', userId) // Exclude current user's own record
        .maybeSingle();

      if (duplicateID) {
        throw new Error('This ID/Passport number is already registered with another account. Please contact support if you believe this is an error.');
      }

      // Encrypt sensitive data (in production, this should be done server-side)
      const encryptedIdNumber = btoa(formData.idNumber);

      // CRITICAL FIX: Upload company registration document FIRST if company purchase
      // This is required because the database constraint requires company_registration_document_url
      // to be set when is_company_purchase = true
      let companyRegistrationDocumentUrl = null;
      let companyRegistrationDocumentFilename = null;

      if (formData.isCompanyPurchase && formData.companyRegistrationDocument) {
        console.log('📤 Uploading company registration document BEFORE KYC insert...');
        try {
          const uploadResult = await uploadKYCDocumentViaAPI(
            formData.companyRegistrationDocument,
            'companyRegistration'
          );
          companyRegistrationDocumentUrl = uploadResult.storagePath;
          companyRegistrationDocumentFilename = formData.companyRegistrationDocument.name;
          console.log('✅ Company registration document uploaded successfully');
        } catch (error) {
          console.error('❌ Failed to upload company registration document:', error);
          throw new Error('Failed to upload company registration document. Please try again.');
        }
      }

      // Validate id_type - ensure it's one of the allowed values and map to database values
      // Database expects: 'national_id', 'passport', 'drivers_license', 'residence_permit', 'work_permit',
      // 'visa', 'voter_id', 'social_security', 'military_id', 'aadhaar', 'sin', 'other', 'OTH',
      // 'international_passport', 'eu_id_card', 'other_government_id'

      // Map form values to database values
      const idTypeMapping: { [key: string]: string } = {
        'national_id_card': 'national_id',  // Form sends 'national_id_card', DB expects 'national_id'
        'passport': 'passport',
        'drivers_license': 'drivers_license',
        'other_government_id': 'other_government_id'
      };

      const idType = idTypeMapping[formData.idType] || 'passport';

      // Prepare KYC data object
      const kycDataToSave = {
        user_id: userId,
        first_name: formData.firstName,
        last_name: formData.lastName,
        date_of_birth: normalizedDateOfBirth, // CRITICAL: Date of birth for age verification (18+ requirement) — strict YYYY-MM-DD, never "" (see normalizeDateOfBirth)
        nationality: formData.nationality,
        id_type: idType,
        id_number_encrypted: encryptedIdNumber,
        id_number_hash: idNumberHash,
        phone_number: formData.phoneNumber,
        email_address: formData.emailAddress,
        street_address: formData.streetAddress,
        city: formData.city,
        province: formData.province,
        postal_code: formData.postalCode,
        country_code: formData.countryCode,
        country_name: formData.countryName,
        is_company_purchase: formData.isCompanyPurchase,
        company_name: formData.isCompanyPurchase ? formData.companyName : null,
        company_registration_number: formData.isCompanyPurchase ? formData.companyRegistrationNumber : null,
        company_registration_document_url: companyRegistrationDocumentUrl,
        company_registration_document_filename: companyRegistrationDocumentFilename,
        data_consent_given: formData.dataConsent,
        privacy_policy_accepted: formData.privacyPolicyAccepted,
        // Server owns kyc_status / rejection_reason / kyc_editable.
        updated_at: new Date().toISOString()
      };

      let kycData;

      // Submit KYC via server-side API (uses service role client to bypass RLS)
      // The SERVER decides INSERT vs UPDATE by checking for existing records (no RLS issues).
      // Client passes existingRecordId as a hint only.
      console.log(`📤 Submitting KYC via API...`, {
        existingRecordId,
        isResubmissionState: isResubmission
      });

      const response = await fetch(`${API_BASE_URL}/api/user/kyc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'submit_kyc',
          data: {
            kycData: kycDataToSave,
            existingKycId: existingRecordId
          }
        })
      });

      const apiResult = await response.json();
      if (!response.ok) {
        throw new Error(kycSubmitApiError(apiResult));
      }

      kycData = apiResult.data?.kyc;

      // Upload documents - CRITICAL: Must succeed for KYC to be valid
      const documentUploads = [];
      const uploadErrors: string[] = [];

      // NOTE: Company registration document is already uploaded above before KYC insert
      // This is required because the database constraint requires company_registration_document_url
      // to be set when is_company_purchase = true
      const docsToUpload = { ...documents };

      console.log('📤 Starting document uploads...');
      for (const [type, doc] of Object.entries(docsToUpload)) {
        if (doc) {
          console.log(`📤 Uploading ${type} to kyc bucket via API...`);
          console.log(`📤 File details:`, {
            name: doc.file.name,
            type: doc.file.type,
            size: doc.file.size
          });

          try {
            // Upload via API endpoint
            const uploadResult = await uploadKYCDocumentViaAPI(doc.file, type);
            console.log(`✅ Successfully uploaded ${type}`);
            documentUploads.push({ type, fileName: uploadResult.storagePath });
          } catch (uploadError) {
            console.error(`❌ Error uploading ${type}:`, uploadError);
            uploadErrors.push(`Failed to upload ${type}: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
          }
        }
      }

      // CRITICAL: If ANY upload failed, throw error and DO NOT mark as submitted
      if (uploadErrors.length > 0) {
        throw new Error(`Document upload failed:\n${uploadErrors.join('\n')}`);
      }

      // Verify ALL required documents were uploaded OR exist from previous submission
      const requiredDocs = ['idDocument', 'selfie', 'addressProof'];
      const uploadedTypes = documentUploads.map(d => d.type);

      // For resubmission, check if existing documents can be used
      const missingDocs = requiredDocs.filter(doc => {
        const hasNewUpload = uploadedTypes.includes(doc);
        const hasExistingDoc = isResubmission && existingDocuments[doc as keyof typeof existingDocuments];
        return !hasNewUpload && !hasExistingDoc;
      });

      if (missingDocs.length > 0) {
        const missingNames = missingDocs.map(d => {
          if (d === 'idDocument') return 'ID Document';
          if (d === 'selfie') return 'Selfie';
          if (d === 'addressProof') return 'Proof of Residence';
          return d;
        });
        throw new Error(`Missing required documents: ${missingNames.join(', ')}. Please upload all required documents.`);
      }

      console.log(`✅ All ${documentUploads.length} documents uploaded successfully`);

      // Log KYC action using regular client (EXECUTE permission granted)
      await supabase.rpc('log_kyc_action', {
        p_kyc_id: kycData.id,
        p_user_id: userId,
        p_action: 'created',
        p_performed_by_username: 'web_dashboard'
      });

      // Show success message ONLY if all uploads succeeded
      alert('KYC information submitted successfully! Your application is now under review.');
      // v7.3.x: wizard finished — drop the persisted step so a future visit starts fresh.
      clearPersistedKycStep(userId);
      onComplete?.(kycData);

    } catch (error: any) {
      console.error('Error submitting KYC:', error);

      let errorMessage = 'Failed to submit KYC information. Please try again.';

      // Handle specific database constraint errors
      if (error?.code === '23505') {
        if (error.message?.includes('id_number_hash')) {
          errorMessage = 'This ID/Passport number is already registered with another account. Please contact support if you believe this is an error.';
        } else if (error.message?.includes('email')) {
          errorMessage = 'This email address is already registered. Please use a different email address.';
        } else {
          errorMessage = 'This information is already registered in our system. Please check your details or contact support.';
        }
      } else if (error?.code === '23503') {
        errorMessage = 'Invalid user account. Please log out and log back in.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const hashIdNumber = async (idNumber: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(idNumber);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h3 style={{ color: '#f59e0b', fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>
              📝 {t('kycUpload.step1.heading', 'Personal Information')}
            </h3>

            {/* Company Purchase Toggle */}
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', color: '#ffffff', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={formData.isCompanyPurchase}
                  onChange={(e) => setFormData(prev => ({ ...prev, isCompanyPurchase: e.target.checked }))}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#3b82f6'
                  }}
                />
                <span>🏢 I am registering shares on behalf of a company</span>
              </label>
              <p style={{ color: '#ffffff', fontSize: '13px', marginTop: '8px', marginLeft: '30px' }}>
                Check this box if you are purchasing shares for a company or organization
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  First Name *
                  {!isDocumentOnlyRejection && validationStatus.firstName === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                  {!isDocumentOnlyRejection && validationStatus.firstName === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Available</span>}
                  {!isDocumentOnlyRejection && validationStatus.firstName === 'invalid' && !isResubmission && <span style={{ color: '#ef4444', fontSize: '13px', marginLeft: '8px' }}>❌ Already used</span>}
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {formData.firstName}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: errors.firstName ? '2px solid #ef4444' :
                             (validationStatus.firstName === 'invalid' && !isResubmission) ? '2px solid #ef4444' :
                             validationStatus.firstName === 'valid' ? '2px solid #22c55e' :
                             '1px solid #4b5563',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                )}
                {!isDocumentOnlyRejection && errors.firstName && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                    {errors.firstName}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  Last Name *
                  {!isDocumentOnlyRejection && validationStatus.lastName === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                  {!isDocumentOnlyRejection && validationStatus.lastName === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Available</span>}
                  {!isDocumentOnlyRejection && validationStatus.lastName === 'invalid' && !isResubmission && <span style={{ color: '#ef4444', fontSize: '13px', marginLeft: '8px' }}>❌ Already used</span>}
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {formData.lastName}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: errors.lastName ? '2px solid #ef4444' :
                             (validationStatus.lastName === 'invalid' && !isResubmission) ? '2px solid #ef4444' :
                             validationStatus.lastName === 'valid' ? '2px solid #22c55e' :
                             '1px solid #4b5563',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                )}
                {!isDocumentOnlyRejection && errors.lastName && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                    {errors.lastName}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  Date of Birth * <span style={{ fontSize: '13px', color: '#ffffff' }}>(you must be 18+ years old)</span>
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {isoDateToDayMonthYear(formData.dateOfBirth)}
                  </div>
                ) : (
                  <KycBirthdateField
                    showLabel={false}
                    text={dobText}
                    error={errors.dateOfBirth || null}
                    minAge={18}
                    minAgeMessage="You must be at least 18 years old to complete KYC verification and purchase shares"
                    onTextChange={(value) => {
                      setDobText(value);
                      if (errors.dateOfBirth) setErrors(prev => ({ ...prev, dateOfBirth: '' }));
                    }}
                    onCommit={(iso) => {
                      setDobText(isoDateToDayMonthYear(iso));
                      setFormData(prev => ({ ...prev, dateOfBirth: iso }));
                      setErrors(prev => ({ ...prev, dateOfBirth: '' }));
                    }}
                    onInvalid={(message) => {
                      setErrors(prev => ({ ...prev, dateOfBirth: message || 'Use day/month/year, for example 12/10/1992' }));
                    }}
                  />
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  Nationality * <span style={{ fontSize: '13px', color: '#ffffff' }}>(where you were born)</span>
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {formData.nationality}
                  </div>
                ) : (
                  <select
                    value={formData.nationality}
                    onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: errors.nationality ? '2px solid #ef4444' : '1px solid #4b5563',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Nationality</option>
                    {nationalities.map(nationality => (
                      <option key={nationality} value={nationality}>
                        {nationality}
                      </option>
                    ))}
                  </select>
                )}
                {errors.nationality && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                    {errors.nationality}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  Country * <span style={{ fontSize: '13px', color: '#ffffff' }}>(where you live)</span>
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {formData.countryName}
                  </div>
                ) : (
                  <>
                    <select
                      value={formData.countryCode}
                      required
                      onChange={(e) => {
                        const country = countries.find(c => c.code === e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          countryCode: e.target.value,
                          countryName: country?.name || ''
                        }));
                        if (errors.countryCode) {
                          setErrors(prev => {
                            const next = { ...prev };
                            delete next.countryCode;
                            return next;
                          });
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.5)',
                        border: errors.countryCode ? '1px solid #ef4444' : '1px solid #4b5563',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select country</option>
                      {countries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    {errors.countryCode && (
                      <div style={{ color: '#f87171', fontSize: '13px', marginTop: '6px' }}>{errors.countryCode}</div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  ID Type *
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {getIdTypeDisplayLabel(formData.idType)}
                  </div>
                ) : (
                  <select
                    value={formData.idType}
                    onChange={(e) => setFormData(prev => ({ ...prev, idType: e.target.value as any }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: '1px solid #4b5563',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select ID Type</option>
                    {getIdTypeOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  {getIdNumberLabel(formData.idType)} *
                  {!isDocumentOnlyRejection && validationStatus.idNumber === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                  {!isDocumentOnlyRejection && validationStatus.idNumber === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Available</span>}
                  {!isDocumentOnlyRejection && validationStatus.idNumber === 'invalid' && !isResubmission && <span style={{ color: '#ef4444', fontSize: '13px', marginLeft: '8px' }}>❌ Already used</span>}
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {formData.idNumber}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.idNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, idNumber: e.target.value }))}
                    placeholder={getIdNumberPlaceholder(formData.idType)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: errors.idNumber ? '2px solid #ef4444' :
                             (validationStatus.idNumber === 'invalid' && !isResubmission) ? '2px solid #ef4444' :
                             validationStatus.idNumber === 'valid' ? '2px solid #22c55e' :
                             '1px solid #4b5563',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                )}
                {!isDocumentOnlyRejection && errors.idNumber && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                    {errors.idNumber}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  Phone Number *
                  {!isDocumentOnlyRejection && validationStatus.phoneNumber === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                  {!isDocumentOnlyRejection && validationStatus.phoneNumber === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Available</span>}
                  {!isDocumentOnlyRejection && validationStatus.phoneNumber === 'invalid' && !isResubmission && <span style={{ color: '#ef4444', fontSize: '13px', marginLeft: '8px' }}>❌ Already used</span>}
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {formData.phoneNumber}
                  </div>
                ) : (
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    placeholder="+27 12 345 6789"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: errors.phoneNumber ? '2px solid #ef4444' :
                             (validationStatus.phoneNumber === 'invalid' && !isResubmission) ? '2px solid #ef4444' :
                             validationStatus.phoneNumber === 'valid' ? '2px solid #22c55e' :
                             '1px solid #4b5563',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                )}
                {!isDocumentOnlyRejection && errors.phoneNumber && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                    {errors.phoneNumber}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  Email Address *
                  {!isDocumentOnlyRejection && validationStatus.emailAddress === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                  {!isDocumentOnlyRejection && validationStatus.emailAddress === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Available</span>}
                  {!isDocumentOnlyRejection && validationStatus.emailAddress === 'invalid' && !isResubmission && <span style={{ color: '#ef4444', fontSize: '13px', marginLeft: '8px' }}>❌ Already used</span>}
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}>
                    {formData.emailAddress}
                  </div>
                ) : (
                  <input
                    type="email"
                    value={formData.emailAddress}
                    onChange={(e) => setFormData(prev => ({ ...prev, emailAddress: e.target.value }))}
                    placeholder="your@email.com"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: errors.emailAddress ? '2px solid #ef4444' :
                             (validationStatus.emailAddress === 'invalid' && !isResubmission) ? '2px solid #ef4444' :
                             validationStatus.emailAddress === 'valid' ? '2px solid #22c55e' :
                             '1px solid #4b5563',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                )}
                {!isDocumentOnlyRejection && errors.emailAddress && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                    {errors.emailAddress}
                  </div>
                )}
              </div>

              {/* Company Information Section - Shown when isCompanyPurchase is checked */}
              {formData.isCompanyPurchase && (
                <>
                  <div style={{
                    gridColumn: '1 / -1',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '8px',
                    padding: '16px',
                    marginTop: '8px'
                  }}>
                    <h4 style={{ color: '#22c55e', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                      🏢 Company Information
                    </h4>
                    <p style={{ color: '#ffffff', fontSize: '13px' }}>
                      Please provide your company details below
                    </p>
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={formData.companyName || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      placeholder="Your Company Ltd"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.5)',
                        border: errors.companyName ? '2px solid #ef4444' : '1px solid #4b5563',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                    {errors.companyName && (
                      <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                        {errors.companyName}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                      Company Registration Number *
                    </label>
                    <input
                      type="text"
                      value={formData.companyRegistrationNumber || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyRegistrationNumber: e.target.value }))}
                      placeholder="e.g., 2023/123456"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.5)',
                        border: errors.companyRegistrationNumber ? '2px solid #ef4444' : '1px solid #4b5563',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                    {errors.companyRegistrationNumber && (
                      <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                        {errors.companyRegistrationNumber}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                      Company Registration Document *
                    </label>
                    <input
                      ref={fileInputRefs.companyRegistration}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setFormData(prev => ({ ...prev, companyRegistrationDocument: file }));
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.5)',
                        border: errors.companyRegistrationDocument ? '2px solid #ef4444' : '1px solid #4b5563',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                    {formData.companyRegistrationDocument && (
                      <div style={{ color: '#22c55e', fontSize: '13px', marginTop: '4px' }}>
                        ✅ {formData.companyRegistrationDocument.name}
                      </div>
                    )}
                    {errors.companyRegistrationDocument && (
                      <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                        {errors.companyRegistrationDocument}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h3 style={{ color: '#f59e0b', fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>
              🏠 {t('kycUpload.step2.heading', 'Address Information')}
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                  Street Address *
                  {!isDocumentOnlyRejection && validationStatus.streetAddress === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                  {!isDocumentOnlyRejection && validationStatus.streetAddress === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Valid</span>}
                </label>
                {isDocumentOnlyRejection ? (
                  <div style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgba(75, 85, 99, 0.3)',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    minHeight: '80px',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {formData.streetAddress}
                  </div>
                ) : (
                  <textarea
                    value={formData.streetAddress}
                    onChange={(e) => setFormData(prev => ({ ...prev, streetAddress: e.target.value }))}
                    placeholder="123 Main Street, Apartment 4B"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(55, 65, 81, 0.5)',
                      border: errors.streetAddress ? '2px solid #ef4444' :
                             validationStatus.streetAddress === 'valid' ? '2px solid #22c55e' :
                             '1px solid #4b5563',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                )}
                {!isDocumentOnlyRejection && errors.streetAddress && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                    {errors.streetAddress}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                    City *
                    {!isDocumentOnlyRejection && validationStatus.city === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                    {!isDocumentOnlyRejection && validationStatus.city === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Valid</span>}
                  </label>
                  {isDocumentOnlyRejection ? (
                    <div style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(75, 85, 99, 0.3)',
                      border: '1px solid #4b5563',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}>
                      {formData.city}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Cape Town"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.5)',
                        border: errors.city ? '2px solid #ef4444' :
                               validationStatus.city === 'valid' ? '2px solid #22c55e' :
                               '1px solid #4b5563',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  )}
                  {!isDocumentOnlyRejection && errors.city && (
                    <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                      {errors.city}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                    Province/State *
                    {!isDocumentOnlyRejection && validationStatus.province === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                    {!isDocumentOnlyRejection && validationStatus.province === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Valid</span>}
                  </label>
                  {isDocumentOnlyRejection ? (
                    <div style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(75, 85, 99, 0.3)',
                      border: '1px solid #4b5563',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}>
                      {formData.province}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.province}
                      onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
                      placeholder="Western Cape"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.5)',
                        border: errors.province ? '2px solid #ef4444' :
                               validationStatus.province === 'valid' ? '2px solid #22c55e' :
                               '1px solid #4b5563',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  )}
                  {!isDocumentOnlyRejection && errors.province && (
                    <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                      {errors.province}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                    Postal Code *
                    {!isDocumentOnlyRejection && validationStatus.postalCode === 'checking' && <span style={{ color: '#fbbf24', fontSize: '13px', marginLeft: '8px' }}>🔍 Checking...</span>}
                    {!isDocumentOnlyRejection && validationStatus.postalCode === 'valid' && <span style={{ color: '#22c55e', fontSize: '13px', marginLeft: '8px' }}>✅ Valid</span>}
                  </label>
                  {isDocumentOnlyRejection ? (
                    <div style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'rgba(75, 85, 99, 0.3)',
                      border: '1px solid #4b5563',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}>
                      {formData.postalCode}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.postalCode}
                      onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                      placeholder="8001"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.5)',
                        border: errors.postalCode ? '2px solid #ef4444' :
                               validationStatus.postalCode === 'valid' ? '2px solid #22c55e' :
                               '1px solid #4b5563',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  )}
                  {!isDocumentOnlyRejection && errors.postalCode && (
                    <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>
                      {errors.postalCode}
                    </div>
                  )}
                </div>


              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h3 style={{ color: '#f59e0b', fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>
              📄 {t('kycUpload.step3.heading', 'Document Upload')}
            </h3>

            {/* v7.3.x: camera fallback notice — shown when the in-app camera can't open */}
            {errors.camera && (
              <div style={{
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.4)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#fbbf24',
                fontSize: '14px'
              }}>
                {errors.camera}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* ID Document Upload */}
              <div style={{
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                borderRadius: '8px',
                padding: '20px',
                border: errors.idDocument ? '2px solid #ef4444' : '1px solid #4b5563'
              }}>
                <h4 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  🆔 {getIdDocumentTitle(formData.idType)}
                </h4>
                <p style={{ color: '#ffffff', fontSize: '14px', marginBottom: '16px' }}>
                  Upload a clear photo of your {getIdDocumentDescription(formData.idType)}.
                  Make sure all text is readable and the image is not blurry.
                </p>

                {/* Show existing document indicator for resubmissions */}
                {isResubmission && existingDocuments.idDocument && !documents.idDocument && (
                  <div style={{
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#22c55e', fontSize: '16px' }}>✅</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#22c55e', fontSize: '14px', fontWeight: '600' }}>
                          Existing document on file
                        </div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>
                          {existingDocuments.idDocument}
                        </div>
                      </div>
                    </div>
                    <p style={{ color: '#ffffff', fontSize: '13px', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                      You can keep this document or upload a new one if it was rejected.
                    </p>
                  </div>
                )}

                {/* Browse input: no `capture` so "Choose File" opens the native picker */}
                <input
                  ref={fileInputRefs.idDocument}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload('id_document', file);
                  }}
                  style={{ display: 'none' }}
                />
                {/* Camera input: image-only + capture, used as the "Take Photo" fallback */}
                <input
                  ref={fileInputRefs.idDocumentCamera}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload('id_document', file);
                  }}
                  style={{ display: 'none' }}
                  capture="environment"
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {isInAppCameraSupported(typeof navigator !== 'undefined' ? navigator : undefined) && (
                      <button
                        onClick={() => captureFromCamera('id_document')}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          border: '1px solid #22c55e',
                          borderRadius: '8px',
                          color: '#4ade80',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        📷 Take Photo
                      </button>
                    )}
                    <button
                      onClick={() => fileInputRefs.idDocument.current?.click()}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid #3b82f6',
                        borderRadius: '8px',
                        color: '#60a5fa',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      📁 Choose File
                    </button>
                  </div>

                  {documents.idDocument && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '12px',
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '8px'
                    }}>
                      {/* Preview thumbnail - show image or PDF icon */}
                      {documents.idDocument.file.type.startsWith('image/') ? (
                        <img
                          src={documents.idDocument.preview}
                          alt="ID Document Preview"
                          onError={(e) => {
                            console.error('❌ Failed to load ID document preview image');
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('✅ ID document preview image loaded successfully');
                          }}
                          style={{
                            width: '60px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #4b5563'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '60px',
                          height: '40px',
                          borderRadius: '4px',
                          border: '1px solid #4b5563',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          fontSize: '20px'
                        }}>
                          📄
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#10b981', fontSize: '14px', fontWeight: '500' }}>
                          ✅ {documents.idDocument.file.name}
                        </div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>
                          {(documents.idDocument.file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <button
                        onClick={() => setDocuments(prev => ({ ...prev, idDocument: undefined }))}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid #ef4444',
                          borderRadius: '4px',
                          color: '#f87171',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {errors.idDocument && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '8px' }}>
                    {errors.idDocument}
                  </div>
                )}
              </div>

              {/* Selfie Upload */}
              <div style={{
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                borderRadius: '8px',
                padding: '20px',
                border: errors.selfie ? '2px solid #ef4444' : '1px solid #4b5563'
              }}>
                <h4 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  🤳 Selfie Verification
                </h4>
                <p style={{ color: '#ffffff', fontSize: '14px', marginBottom: '16px' }}>
                  Take a clear selfie holding your ID document next to your face.
                  Ensure both your face and the ID document are clearly visible.
                </p>
                <KycSelfieExample />

                {/* Show existing document indicator for resubmissions */}
                {isResubmission && existingDocuments.selfie && !documents.selfie && (
                  <div style={{
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#22c55e', fontSize: '16px' }}>✅</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#22c55e', fontSize: '14px', fontWeight: '600' }}>
                          Existing selfie on file
                        </div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>
                          {existingDocuments.selfie}
                        </div>
                      </div>
                    </div>
                    <p style={{ color: '#ffffff', fontSize: '13px', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                      You can keep this selfie or upload a new one if it was rejected.
                    </p>
                  </div>
                )}

                {/* Browse input: no `capture` so "Upload Selfie" opens the native picker */}
                <input
                  ref={fileInputRefs.selfie}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload('selfie', file);
                  }}
                  style={{ display: 'none' }}
                />
                {/* Camera input: image-only + front-camera capture, used as the "Take Selfie" fallback */}
                <input
                  ref={fileInputRefs.selfieCamera}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload('selfie', file);
                  }}
                  style={{ display: 'none' }}
                  capture="user"
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {isInAppCameraSupported(typeof navigator !== 'undefined' ? navigator : undefined) && (
                      <button
                        onClick={() => captureFromCamera('selfie')}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          border: '1px solid #22c55e',
                          borderRadius: '8px',
                          color: '#4ade80',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        📷 Take Selfie
                      </button>
                    )}
                    <button
                      onClick={() => fileInputRefs.selfie.current?.click()}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid #3b82f6',
                        borderRadius: '8px',
                        color: '#60a5fa',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      📁 Upload Selfie
                    </button>
                  </div>

                  {documents.selfie && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '12px',
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '8px'
                    }}>
                      {/* Preview thumbnail - show image or PDF icon */}
                      {documents.selfie.file.type.startsWith('image/') ? (
                        <img
                          src={documents.selfie.preview}
                          alt="Selfie Preview"
                          onError={(e) => {
                            console.error('❌ Failed to load selfie preview image');
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('✅ Selfie preview image loaded successfully');
                          }}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '2px solid #22c55e'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          border: '2px solid #22c55e',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          fontSize: '24px'
                        }}>
                          📄
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#10b981', fontSize: '14px', fontWeight: '500' }}>
                          ✅ {documents.selfie.file.name}
                        </div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>
                          {(documents.selfie.file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setDocuments(prev => ({ ...prev, selfie: undefined }));
                          setSelfieIdConfirmed(false);
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid #ef4444',
                          borderRadius: '4px',
                          color: '#f87171',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '14px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selfieIdConfirmed}
                    onChange={(e) => {
                      setSelfieIdConfirmed(e.target.checked);
                      if (e.target.checked) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.selfieIdConfirm;
                          return next;
                        });
                      }
                    }}
                    style={{ marginTop: '3px', width: '16px', height: '16px' }}
                  />
                  <span style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.5' }}>
                    {SELFIE_ID_CONFIRM_LABEL}
                  </span>
                </label>
                {errors.selfie && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '8px' }}>
                    {errors.selfie}
                  </div>
                )}
                {errors.selfieIdConfirm && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '8px' }}>
                    {errors.selfieIdConfirm}
                  </div>
                )}
              </div>

              {/* Address Proof Upload */}
              <div style={{
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                borderRadius: '8px',
                padding: '20px',
                border: errors.addressProof ? '2px solid #ef4444' : '1px solid #4b5563'
              }}>
                <h4 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  🏠 Proof of Address <span style={{ color: '#ef4444', fontSize: '14px' }}>*Required</span>
                </h4>

                {/* Option 1: Official Document */}
                <div style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '12px'
                }}>
                  <p style={{ color: '#60a5fa', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                    📄 Option 1: Official Document
                  </p>
                  <p style={{ color: '#ffffff', fontSize: '13px', marginBottom: '4px' }}>
                    Upload a recent utility bill, bank statement, or official document showing your current address.
                  </p>
                  <p style={{ color: '#f59e0b', fontSize: '13px', fontStyle: 'italic' }}>
                    ⚠️ Document must be dated within the last 3 months.
                  </p>
                </div>

                {/* Option 2: Handwritten Declaration */}
                <div style={{
                  backgroundColor: 'rgba(168, 85, 247, 0.1)',
                  border: '1px solid rgba(168, 85, 247, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <p style={{ color: '#a855f7', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                    📝 Option 2: Handwritten Address Declaration
                  </p>
                  <p style={{ color: '#ffffff', fontSize: '13px', marginBottom: '8px' }}>
                    If you don't have an official document, you can create a handwritten declaration:
                  </p>
                  <ol style={{ color: '#ffffff', fontSize: '13px', margin: '0', paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Write your <strong style={{ color: '#ffffff' }}>full current address</strong> on a piece of paper</li>
                    <li>Add <strong style={{ color: '#ffffff' }}>today's date</strong> on the paper</li>
                    <li>Sign the paper with your <strong style={{ color: '#ffffff' }}>signature</strong></li>
                    <li>Place your <strong style={{ color: '#ffffff' }}>ID document</strong> next to the paper</li>
                    <li>Take a <strong style={{ color: '#ffffff' }}>photo</strong> showing both the paper and your ID clearly</li>
                  </ol>
                </div>

                {/* Show existing document indicator for resubmissions */}
                {isResubmission && existingDocuments.addressProof && !documents.addressProof && (
                  <div style={{
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#22c55e', fontSize: '16px' }}>✅</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#22c55e', fontSize: '14px', fontWeight: '600' }}>
                          Existing proof of address on file
                        </div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>
                          {existingDocuments.addressProof}
                        </div>
                      </div>
                    </div>
                    <p style={{ color: '#ffffff', fontSize: '13px', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                      You can keep this document or upload a new one if it was rejected.
                    </p>
                  </div>
                )}

                {/* Browse input: no `capture` so "Choose File" opens the native picker */}
                <input
                  ref={fileInputRefs.addressProof}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload('address_proof', file);
                  }}
                  style={{ display: 'none' }}
                />
                {/* Camera input: image-only + capture, used as the "Take Photo" fallback */}
                <input
                  ref={fileInputRefs.addressProofCamera}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload('address_proof', file);
                  }}
                  style={{ display: 'none' }}
                  capture="environment"
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {isInAppCameraSupported(typeof navigator !== 'undefined' ? navigator : undefined) && (
                      <button
                        onClick={() => captureFromCamera('address_proof')}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          border: '1px solid #22c55e',
                          borderRadius: '8px',
                          color: '#4ade80',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        📷 Take Photo
                      </button>
                    )}
                    <button
                      onClick={() => fileInputRefs.addressProof.current?.click()}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid #3b82f6',
                        borderRadius: '8px',
                        color: '#60a5fa',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      📁 Choose File
                    </button>
                  </div>

                  {documents.addressProof && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '12px',
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '8px'
                    }}>
                      {/* Preview thumbnail - show image or PDF icon */}
                      {documents.addressProof.file.type.startsWith('image/') ? (
                        <img
                          src={documents.addressProof.preview}
                          alt="Address Proof Preview"
                          onError={(e) => {
                            console.error('❌ Failed to load address proof preview image');
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('✅ Address proof preview image loaded successfully');
                          }}
                          style={{
                            width: '60px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #4b5563'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '60px',
                          height: '40px',
                          borderRadius: '4px',
                          border: '1px solid #4b5563',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          fontSize: '20px'
                        }}>
                          📄
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#10b981', fontSize: '14px', fontWeight: '500' }}>
                          ✅ {documents.addressProof.file.name}
                        </div>
                        <div style={{ color: '#ffffff', fontSize: '13px' }}>
                          {(documents.addressProof.file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <button
                        onClick={() => setDocuments(prev => ({ ...prev, addressProof: undefined }))}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid #ef4444',
                          borderRadius: '4px',
                          color: '#f87171',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {errors.addressProof && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '8px' }}>
                    {errors.addressProof}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h3 style={{ color: '#f59e0b', fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>
              ✅ {t('kycUpload.step4.heading', 'Review & Consent')}
            </h3>

            {/* Data Summary */}
            <div style={{
              backgroundColor: 'rgba(55, 65, 81, 0.5)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid #4b5563'
            }}>
              <h4 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                📋 Information Summary
              </h4>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px',
                fontSize: '14px'
              }}>
                <div>
                  <span style={{ color: '#ffffff' }}>Name:</span>
                  <div style={{ color: '#ffffff', fontWeight: '500' }}>
                    {formData.firstName} {formData.lastName}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#ffffff' }}>ID Type:</span>
                  <div style={{ color: '#ffffff', fontWeight: '500' }}>
                    {getIdTypeDisplayLabel(formData.idType)}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#ffffff' }}>Country:</span>
                  <div style={{ color: '#ffffff', fontWeight: '500' }}>{formData.countryName}</div>
                </div>
              </div>

              {/* Document Upload Status */}
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #4b5563' }}>
                <h5 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '12px' }}>📎 Uploaded Documents:</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {documents.idDocument ? (
                      <span style={{ color: '#10b981' }}>✅ ID Document</span>
                    ) : (
                      <span style={{ color: '#ef4444' }}>❌ ID Document (REQUIRED)</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {documents.selfie ? (
                      <span style={{ color: '#10b981' }}>✅ Selfie</span>
                    ) : (
                      <span style={{ color: '#ef4444' }}>❌ Selfie (REQUIRED)</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {documents.addressProof ? (
                      <span style={{ color: '#10b981' }}>✅ Proof of Residence</span>
                    ) : (
                      <span style={{ color: '#ef4444' }}>❌ Proof of Residence (REQUIRED)</span>
                    )}
                  </div>
                  {formData.isCompanyPurchase && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {formData.companyRegistrationDocument ? (
                        <span style={{ color: '#10b981' }}>✅ Company Registration Document</span>
                      ) : (
                        <span style={{ color: '#ef4444' }}>❌ Company Registration Document (REQUIRED)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Privacy Consent */}
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: '#60a5fa', fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                🔒 Privacy & Data Consent
              </h4>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.dataConsent}
                    onChange={(e) => setFormData(prev => ({ ...prev, dataConsent: e.target.checked }))}
                    style={{ marginTop: '2px' }}
                  />
                  <span style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6' }}>
                    I consent to the collection, processing, and storage of my personal information
                    for KYC verification and regulatory compliance purposes. I understand that this
                    information will be used to verify my identity and generate share certificates.
                  </span>
                </label>
                {errors.dataConsent && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px', marginLeft: '24px' }}>
                    {errors.dataConsent}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.privacyPolicyAccepted}
                    onChange={(e) => setFormData(prev => ({ ...prev, privacyPolicyAccepted: e.target.checked }))}
                    style={{ marginTop: '2px' }}
                  />
                  <span style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6' }}>
                    I have read and accept the{' '}
                    <button
                      type="button"
                      onClick={() => setInlineLegalDoc('privacy')}
                      style={{ background: 'none', border: 'none', padding: 0, color: '#60a5fa', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                    >
                      Privacy Policy
                    </button>{' '}
                    and{' '}
                    <button
                      type="button"
                      onClick={() => setInlineLegalDoc('terms')}
                      style={{ background: 'none', border: 'none', padding: 0, color: '#60a5fa', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                    >
                      Terms of Service
                    </button>.
                  </span>
                </label>
                {errors.privacyPolicyAccepted && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px', marginLeft: '24px' }}>
                    {errors.privacyPolicyAccepted}
                  </div>
                )}
              </div>
            </div>

            {/* Important Notice */}
            <div style={{
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <div style={{ color: '#f59e0b', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                ⚠️ Important Notice
              </div>
              <p style={{ color: '#ffffff', fontSize: '13px', lineHeight: '1.6', margin: 0 }}>
                Your KYC information will be reviewed within 24-48 hours. Once approved,
                your share certificates will be generated and sent to your registered email address.
                Please ensure all information is accurate as changes may require re-verification.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'rgba(31, 41, 55, 0.95)',
        borderRadius: '16px',
        padding: '32px',
        border: '1px solid #374151',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: '#f59e0b', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            🆔 {t('kycUpload.heading', 'KYC Verification')} {isResubmission && `(${t('kycUpload.resubmission', 'Resubmission')})`}
          </h2>
          <button
            onClick={onCancel}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>

        {/* Rejection Reason Banner (only shown for resubmissions) */}
        {isResubmission && rejectionReason && (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <h3 style={{ color: '#ef4444', fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
                  {t('kycUpload.rejectedHeading', 'Application Rejected - Corrections Required')}
                </h3>
                <p style={{ color: '#fca5a5', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                  {rejectionReason}
                </p>
                <p style={{ color: '#ffffff', fontSize: '13px', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                  Please review and correct the information below, then resubmit your application.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            {[1, 2, 3, 4].map(step => (
              <div key={step} style={{
                flex: 1,
                textAlign: 'center',
                color: step <= currentStep ? '#f59e0b' : '#6b7280',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                {t('kycUpload.step', 'Step')} {step}
              </div>
            ))}
          </div>
          <div style={{
            width: '100%',
            height: '4px',
            backgroundColor: 'rgba(55, 65, 81, 0.5)',
            borderRadius: '2px'
          }}>
            <div style={{
              width: `${(currentStep / 4) * 100}%`,
              height: '100%',
              backgroundColor: '#f59e0b',
              borderRadius: '2px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Step Content */}
        {renderStep()}

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px' }}>
          <button
            onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : onCancel?.()}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              border: '1px solid #4b5563',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            {currentStep > 1 ? `← ${t('kycUpload.previous', 'Previous')}` : t('kycUpload.cancel', 'Cancel')}
          </button>

          <button
            onClick={async () => {
              if (validateStep(currentStep)) {
                // For step 1, perform duplicate checks before proceeding
                if (currentStep === 1) {
                  setLoading(true);
                  try {
                    // Check for duplicate email via API (exclude current user's own record for resubmissions)
                    const emailCheckResponse = await fetch(`${API_BASE_URL}/api/user/kyc`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
                      body: JSON.stringify({
                        userId,
                        operation: 'check_email_duplicate',
                        data: {
                          email: formData.emailAddress.trim().toLowerCase(),
                          excludeUserId: isResubmission ? userId : null
                        }
                      })
                    });

                    const emailCheckResult = await emailCheckResponse.json();
                    if (emailCheckResult.data?.hasDuplicate) {
                      // Don't show error for document-only rejections (user's own data is correct)
                      if (!isDocumentOnlyRejection) {
                        setErrors(prev => ({ ...prev, emailAddress: '❌ This email address has already been used for KYC verification' }));
                        setLoading(false);
                        return;
                      }
                    }

                    // Check for duplicate phone via API (exclude current user's own record for resubmissions)
                    const normalizedPhone = formData.phoneNumber.replace(/[\s\-()]/g, '');
                    const phoneCheckResponse = await fetch(`${API_BASE_URL}/api/user/kyc`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
                      body: JSON.stringify({
                        userId,
                        operation: 'check_phone_duplicate',
                        data: {
                          phone: normalizedPhone,
                          excludeUserId: isResubmission ? userId : null
                        }
                      })
                    });

                    const phoneCheckResult = await phoneCheckResponse.json();
                    if (phoneCheckResult.data?.hasDuplicate) {
                      setErrors(prev => ({ ...prev, phoneNumber: '❌ This phone number has already been used for KYC verification' }));
                      setLoading(false);
                      return;
                    }

                    // If all checks pass, proceed to next step
                    setCurrentStep(currentStep + 1);
                  } catch (error: any) {
                    console.error('Duplicate check error:', error);
                    setErrors(prev => ({ ...prev, submit: 'Error checking for duplicates. Please try again.' }));
                  } finally {
                    setLoading(false);
                  }
                } else if (currentStep < 4) {
                  setCurrentStep(currentStep + 1);
                } else {
                  handleSubmit();
                }
              }
            }}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: loading ? 'rgba(55, 65, 81, 0.5)' : '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? t('kycUpload.checking', 'Checking...') : currentStep < 4 ? `${t('kycUpload.next', 'Next')} →` : t('kycUpload.submit', 'Submit KYC')}
          </button>
        </div>

        {errors.submit && (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '16px',
            color: '#f87171'
          }}>
            ❌ {errors.submit}
          </div>
        )}

        {/* Show missing documents warning on step 3 */}
        {currentStep === 3 && (errors.idDocument || errors.selfie || errors.selfieIdConfirm || errors.addressProof || (formData.isCompanyPurchase && errors.companyRegistrationDocument)) && (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginTop: '16px'
          }}>
            <div style={{ color: '#f87171', fontWeight: '600', marginBottom: '8px' }}>
              ⚠️ Missing Required Documents
            </div>
            <div style={{ color: '#f87171', fontSize: '14px' }}>
              You must upload ALL required documents before proceeding:
            </div>
            <ul style={{ color: '#f87171', fontSize: '14px', marginTop: '8px', paddingLeft: '20px' }}>
              {errors.idDocument && <li>ID Document</li>}
              {errors.selfie && <li>Selfie</li>}
              {errors.selfieIdConfirm && <li>{SELFIE_ID_CONFIRM_REQUIRED}</li>}
              {errors.addressProof && <li>Proof of Residence</li>}
              {formData.isCompanyPurchase && errors.companyRegistrationDocument && <li>Company Registration Document</li>}
            </ul>
          </div>
        )}

        {/* Camera Modal */}
        {showCameraModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'rgba(31, 41, 55, 0.95)',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid #374151',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ color: '#f59e0b', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                  📷 {currentCameraType === 'selfie' ? 'Take Selfie' :
                       currentCameraType === 'id_document' ? 'Capture ID Document' :
                       'Capture Address Proof'}
                </h3>
                <button
                  onClick={closeCameraModal}
                  style={{
                    padding: '8px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '20px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Camera Preview or Captured Photo */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: '400px',
                backgroundColor: '#000',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '20px'
              }}>
                {!capturedPhoto ? (
                  <video
                    id="camera-video"
                    ref={(video) => {
                      if (video && cameraStream) {
                        video.srcObject = cameraStream;
                        video.play().catch(error => {
                          console.log('Video play error (expected in dev mode):', error);
                        });
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    autoPlay
                    playsInline
                    muted
                  />
                ) : (
                  <img
                    src={capturedPhoto}
                    alt="Captured photo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                )}

                {/* Overlay instructions */}
                {!capturedPhoto && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    right: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    {currentCameraType === 'selfie'
                      ? 'Hold your ID document next to your face and ensure both are clearly visible'
                      : currentCameraType === 'id_document'
                      ? 'Position your ID document within the frame and ensure all text is readable'
                      : 'Position your address document within the frame and ensure all text is readable'
                    }
                  </div>
                )}
              </div>

              {/* Control Buttons */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                {!capturedPhoto ? (
                  <>
                    <button
                      onClick={closeCameraModal}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: 'transparent',
                        border: '1px solid #6b7280',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={capturePhoto}
                      style={{
                        padding: '12px 32px',
                        backgroundColor: '#22c55e',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      📷 Capture
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={retakePhoto}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: 'transparent',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        color: '#f59e0b',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      🔄 Retake
                    </button>
                    <button
                      onClick={usePhoto}
                      style={{
                        padding: '12px 32px',
                        backgroundColor: '#22c55e',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ✅ Use Photo
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {inlineLegalDoc && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={inlineLegalDoc === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 80,
              background: 'rgba(0,0,0,0.72)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 720,
                maxHeight: '90vh',
                overflowY: 'auto',
                background: '#121212',
                border: '1px solid rgba(250,204,21,0.35)',
                borderRadius: 12,
                padding: 20,
                color: '#F9FAFB',
              }}
            >
              <button
                type="button"
                onClick={() => setInlineLegalDoc(null)}
                style={{
                  float: 'right',
                  background: 'transparent',
                  border: '1px solid #4B5563',
                  color: '#D1D5DB',
                  borderRadius: 8,
                  padding: '6px 12px',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
              {inlineLegalDoc === 'privacy' ? <PrivacyPolicy /> : <TermsAndConditions />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
